import { useEffect, useRef, useState } from 'react';
import { MediFindMoments } from '@medifind/ads';
import {
  ClinicalTrialAbstract,
  ClinicalTrialAuthors,
  ClinicalTrialContacts,
  ClinicalTrialDates,
  ClinicalTrialElement,
  ClinicalTrialEligibility,
  ClinicalTrialGenInfo,
  ClinicalTrialLocations,
  ClinicalTrialParticipants,
  ClinicalTrialRelatedConditions,
  ClinicalTrialSource,
  ClinicalTrialSponsors,
  ClinicalTrialTreatments,
  SimilarClinicalTrials,
  articleSharedStyles,
} from '@medifind/articles';
import { BlockBody, BlockContainer, BlockHeader, GridCol, GridContainer, GridRow } from '@medifind/containers';
import { useRouteQueryResponse } from '@medifind/hooks';
import { getTrial } from '@medifind/interface';
import { Share } from '@medifind/popups';
import { usePageHooks, useRouter } from '@medifind/router';
import { SEO } from '@medifind/seo';
import { BackButton, Bookmark, MFLazyLoad, Skeleton, useFeatureFlag } from '@medifind/shared-basic-components';
import { getValueMediaBreakpointDown, useMediaQueryDown } from '@medifind/utils';
import { selectRouteParams, setShareBookmarkData, useLocationStore, useRouteStore } from '@medifind/zustand';

/* __ROUTE_PATH__=/(conditions|procedures)/:codingSlug/:projectId/clinical-trial/:articleId */
/* __ROUTE_PATH__=/articles/clinical-trial/:articleId */
const TrialsPageArticle = () => {
  const router = useRouter();
  const { projectId, codingSlug, articleId } = router.query;
  const { lat, lon, fidelity, countryCode } = useLocationStore();
  // const [doctors, setDoctors] = useState(null);
  const { codingName, codingType } = useRouteStore(selectRouteParams);
  const [openLocationsByScroll, setOpenLocationsByScroll] = useState(false);
  const FEATURE_FLAG_TRIAL_CONNECT = useFeatureFlag('TRIAL_CONNECT');
  const mediumBreakpoint = useMediaQueryDown('md');
  const query = {
    id: articleId,
    lat,
    lon,
    fidelity,
    countryCode,
    projectId,
  };
  const { result: trial, notFound } = useRouteQueryResponse({
    queryKey: ['getTrial', articleId, projectId],
    queryFn: async () => await getTrial(query),
  });

  usePageHooks(trial);
  const locationRef = useRef(null);
  const handleLocationScroll = () => {
    if (!locationRef.current) return;
    setOpenLocationsByScroll(true);
    locationRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (openLocationsByScroll) {
      setOpenLocationsByScroll(false);
    }
  }, [openLocationsByScroll]);

  useEffect(() => {
    setShareBookmarkData({
      bookmark: {
        bookmarkName: trial ? trial.title : '',
        data: trial?.clinicalTrialId,
        ...(projectId && codingName
          ? {
              secondaryLookupId: projectId,
              secondaryLookupData: {
                projectId,
                codingSlug,
                codingType,
                codingName,
                type: 'projectId',
              },
            }
          : {}),
        type: 'trial',
      },
      share: {
        bookmarkName: trial ? trial.title : '',
        personifyId: trial?.clinicalTrialId,
        data: trial?.clinicalTrialId,
        projectId,
        codingSlug,
        codingName,
        type: 'trial',
      },
    });
  }, [trial, projectId, codingSlug, codingName, codingType]);

  const getTitle = () => {
    if (!trial) {
      return <Skeleton count={getValueMediaBreakpointDown({ xs: 6, sm: 5, md: 4, default: 4 })} />;
    }
    const titleText = trial?.title || 'Title Not Found';
    return projectId ? <h2>{titleText}</h2> : <h1>{titleText}</h1>;
  };

  if (notFound) {
    throw new Error(404);
  }
  return (
    <>
      {trial && (
        <SEO
          noIndex={notFound}
          title={trial.title ? `${trial.title} | MediFind` : `MediFind`}
          canonical={process.env.NX_LOCAL_HOST + `/articles/clinical-trial/${trial.clinicalTrialId}`}
        />
      )}
      <GridContainer>
        <GridRow>
          <GridCol xl={8}>
            <MediFindMoments type={'leaderboard'} targeting={{ pageType: 'clinical-trial-article', position: 'top' }} />
            <BlockContainer className={articleSharedStyles['article']}>
              <BlockHeader className={articleSharedStyles['article__header']}>
                <BackButton label={mediumBreakpoint ? 'Back' : 'Back to results'} color="transparent-blue" buttonType="box" />
                <div style={{ margin: 0, padding: 0, marginLeft: 'auto' }} />
                <Bookmark color="transparent-blue" buttonType="box" iconOnlyAt="xs" />
                <Share color="transparent-blue" buttonType="box" type="trial" iconOnlyAt="xs" />
              </BlockHeader>
              <BlockBody className={articleSharedStyles['article__body']}>
                <div className={articleSharedStyles['article__title']}>{getTitle()}</div>
                <ClinicalTrialGenInfo
                  isLoading={!trial}
                  interventionType={trial?.interventionType}
                  studyType={trial?.studyType}
                  phase={trial?.phase}
                  patientPopulation={trial?.patientPopulation}
                  primaryTreatment={trial?.primaryTreatment}
                  status={trial?.status}
                  addressCount={trial?.addresses?.addressCount}
                  closestLocation={trial?.closestLocation}
                  trialName={trial?.title}
                  contacts={trial?.contacts || []}
                  handleLocationScroll={() => handleLocationScroll()}
                />
                {/* MediFind Summary */}
                {!trial || trial.cleanedAbstract ? <ClinicalTrialAbstract isLoading={!trial} abstract={trial?.cleanedAbstract} /> : null}
                {/* Eligibilities */}
                {!trial || (trial.eligibility && trial.eligibility.length > 0) ? (
                  <ClinicalTrialEligibility eligibility={trial?.eligibility} isLoading={!trial} />
                ) : null}
                {/* Locations */}
                {!trial || (trial?.addresses?.locationsUS && trial?.addresses?.otherLocations) ? (
                  <ClinicalTrialLocations
                    ref={locationRef}
                    openByScroll={openLocationsByScroll}
                    locationsUS={trial?.addresses?.locationsUS || []}
                    otherLocations={trial?.addresses?.otherLocations || []}
                    contacts={trial?.contacts || []}
                    isLoading={!trial}
                    trialName={trial?.title}
                  />
                ) : null}
                {/* Contacts */}
                {!trial || (trial.contacts && trial.contacts.length > 0) ? (
                  <ClinicalTrialContacts contacts={trial?.contacts} isLoading={!trial} />
                ) : null}
                {/* Trial Dates*/}
                {!trial || trial.completionDate || trial.startDate ? (
                  <ClinicalTrialDates
                    startDate={trial?.startDate}
                    completionDate={trial?.completionDate}
                    isLoading={!trial}
                    status={trial?.status}
                  />
                ) : null}
                {/* Number of Participants*/}
                {!trial || trial.numParticipants ? (
                  <ClinicalTrialParticipants participants={trial?.numParticipants} isLoading={!trial} />
                ) : null}
                {/* Treatments Section*/}
                {!trial || trial.designGroups ? <ClinicalTrialTreatments treatments={trial?.designGroups} isLoading={!trial} /> : null}
                {/* Authors */}
                {!trial || (trial.authors && trial.authors.length > 0) ? (
                  <ClinicalTrialAuthors
                    authors={trial?.authors}
                    isLoading={!trial}
                    codingType={codingType}
                    projectId={projectId}
                    codingSlug={codingSlug}
                  />
                ) : null}
                {/* Relevant Conditions */}
                {!trial || (trial.conditions && trial.conditions.length > 0) ? (
                  <ClinicalTrialRelatedConditions conditions={trial?.conditions} isLoading={!trial} />
                ) : null}
                {/* Sponsors Section*/}
                {!trial || (trial.sponsors && trial.sponsors.length > 0) ? (
                  <ClinicalTrialSponsors sponsors={trial?.sponsors} isLoading={!trial} />
                ) : null}
              </BlockBody>
            </BlockContainer>
            {/* View Article From Source */}
            {!trial || trial.url ? <ClinicalTrialSource url={trial?.url} trialId={trial?.clinicalTrialId} isLoading={!trial} /> : null}
          </GridCol>
          <GridCol xl={4}>
            {FEATURE_FLAG_TRIAL_CONNECT && (!trial || trial.status === 'Recruiting') && trial?.closestLocation ? (
              <ClinicalTrialElement
                status={trial?.status}
                closestLocation={trial?.closestLocation}
                trialName={trial?.title}
                contacts={trial?.contacts}
              />
            ) : null}
            {/* Similar Clinical Trials */}
            <MFLazyLoad height={2000} noSuspense resize>
              {/* {trial && ( */}
              <SimilarClinicalTrials docId={trial?.docId} size={3} />
              {/* )} */}
            </MFLazyLoad>
          </GridCol>
        </GridRow>
      </GridContainer>
    </>
  );
};

export default TrialsPageArticle;
